import type { AnyRouter } from '@tanstack/react-router';
import { api } from '@/api/client';
import { getSessionUser } from '@/lib/supabase';
import { isOneTimeRoute } from '@/lib/one-time-routes';
import { consumeNavSource, type NavSource } from './nav-source';

const SESSION_KEY = 'fb_nav_session_id';
const SESSION_LAST_KEY = 'fb_nav_session_last_at';
const SESSION_IDLE_MS = 30 * 60 * 1000;
const DEBOUNCE_MS = 300;

const EXCLUDED_PREFIXES = ['/login', '/signup', '/auth/', '/404', '/error'];

let lastNavAt = 0;
let lastPathname: string | null = null;
let pendingTimer: ReturnType<typeof setTimeout> | null = null;

let burstNavSource: NavSource | null = null;

/**
 * The Community tab a location shows, or null off /community. A tab change on /community is a
 * search change, not a path change, so it's tracked separately: the baseline of Chat use before
 * Chat moves to its own menu item (Speedies, F-129).
 */
function communityTabOf(loc?: { pathname: string; search?: unknown }): string | null {
  if (!loc || loc.pathname.replace(/\/+$/, '') !== '/community') return null;
  const tab = (loc.search as Record<string, unknown> | undefined)?.tab;
  return typeof tab === 'string' && tab ? tab : 'default';
}

export function getNavSessionId(): string {
  return getOrCreateSessionId();
}

function getOrCreateSessionId(): string {
  const now = Date.now();
  const lastAt = Number(sessionStorage.getItem(SESSION_LAST_KEY) || 0);
  const existing = sessionStorage.getItem(SESSION_KEY);
  if (existing && lastAt > 0 && now - lastAt < SESSION_IDLE_MS) {
    sessionStorage.setItem(SESSION_LAST_KEY, String(now));
    return existing;
  }
  const fresh =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${now}-${Math.random().toString(36).slice(2)}`;
  sessionStorage.setItem(SESSION_KEY, fresh);
  sessionStorage.setItem(SESSION_LAST_KEY, String(now));
  return fresh;
}

function isExcluded(pathname: string): boolean {
  return EXCLUDED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
}

async function emit(
  pathname: string,
  searchStr: string,
  referrer: string | null,
  durationMs: number,
  tab: string | null,
  navSource: NavSource,
): Promise<void> {
  try {
    // Guarded routes render briefly pre-login before the root redirect, and
    // public pages (privacy, password reset) are reachable logged-out — there
    // is no user to attribute those navigations to, so skip them.
    if (!(await getSessionUser())) return;
    await api.logActivity({
      type: 'navigation',
      action: 'navigated',
      entity_type: 'page',
      entity_id: pathname,
      entity_name: pathname,
      description: `Navigated to ${pathname}`,
      metadata: {
        search: searchStr || undefined,
        referrer: referrer || undefined,
        session_id: getOrCreateSessionId(),
        duration_ms_from_previous: durationMs > 0 ? durationMs : undefined,
        tab: tab ?? undefined,
        nav_source: navSource,
      },
    });
  } catch {
    // Unauthenticated or transient — never block navigation, never surface UI.
  }
}

export function attachNavTracker(router: AnyRouter): () => void {
  const unsubscribe = router.subscribe('onResolved', (event) => {
    const to = event?.toLocation;
    if (!to) return;
    const toTab = communityTabOf(to);
    const tabChanged = toTab !== null && communityTabOf(event.fromLocation) !== toTab;
    if (!event.pathChanged && !tabChanged) return;
    const pathname = to.pathname;
    if (isExcluded(pathname)) return;
    // Taken now, not when the debounce fires: the click that started this navigation set it.
    // Within a 300 ms burst the first marked source wins (a redirect after a menu click is unmarked).
    const marked = consumeNavSource();
    burstNavSource = burstNavSource && burstNavSource !== 'deeplink' ? burstNavSource : marked;
    const navSource = burstNavSource;

    if (pendingTimer) clearTimeout(pendingTimer);
    pendingTimer = setTimeout(() => {
      const now = Date.now();
      const durationMs = lastNavAt > 0 ? now - lastNavAt : 0;
      const referrer = lastPathname;
      lastPathname = pathname;
      lastNavAt = now;
      // A link page reopened without its link is a dead end: never restore one.
      if (!isOneTimeRoute(pathname)) {
        try { localStorage.setItem('fb-last-route', pathname); } catch { /* private browsing */ }
      }
      burstNavSource = null;
      void emit(pathname, to.searchStr ?? '', referrer, durationMs, toTab, navSource);
    }, DEBOUNCE_MS);
  });

  return () => {
    if (pendingTimer) clearTimeout(pendingTimer);
    unsubscribe();
  };
}
